import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, Empty, Select, Space, Tag } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import {
  getRequest,
  patchRequest,
  dutyTransactionsUrl,
  successNotification,
  errorNotification
} from '../config'
import {
  PAYMENT_MODE_COLORS,
  PAYMENT_MODE_LABELS,
  claimedName
} from '../dashboard/Transactions/utils'

const { Option } = Select

// Mirrors the backend payments constants (STATUS_LIST), as the conversation page does.
const STATUS_OPTIONS = ['INITIALIZED', 'PROCESSING', 'SUCCESSFUL', 'CANCELLED']

const txnColor = status =>
  status === 'SUCCESSFUL' ? 'green' : status === 'CANCELLED' ? 'red' : 'gold'

/**
 * The payments on one vehicle-history order or one shipment, with the same approval the duty
 * conversation page has had for a year (owner, 2026-10-30).
 *
 * Why it exists: a customer who picks "MoMo to our number" sends the money themselves, so nothing
 * tells us it arrived — a person has to confirm it against the statement. On duty that person has
 * a status Select; on these two services the payment card was read-only, so the owner could see
 * the claim and do nothing about it.
 *
 * Marking one SUCCESSFUL is not a cosmetic edit: the server settles it through the payment layer,
 * which runs the service's own hook — the report asks the customer to confirm the VIN, the
 * shipment starts. `onChanged` is how the page around this reloads to show that.
 */
const ServiceTransactions = ({ service, reference, onChanged, title }) => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState({})
  const [savingId, setSavingId] = useState(null)

  const load = useCallback(async () => {
    if (!reference) {
      setRows([])
      return
    }
    const res = await getRequest(
      `${dutyTransactionsUrl}/?service=${service}&search=${encodeURIComponent(reference)}`,
      'payments',
      setLoading
    )
    if (res.success) setRows(res.data.results || res.data || [])
  }, [service, reference])

  useEffect(() => {
    load()
  }, [load])

  const save = async txn => {
    const status = draft[txn.id] || txn.status
    if (status === txn.status) return
    setSavingId(txn.id)
    const res = await patchRequest(
      `${dutyTransactionsUrl}/${txn.id}/`,
      { status },
      'payment',
      () => {}
    )
    setSavingId(null)
    if (res.success) {
      successNotification(
        status === 'SUCCESSFUL'
          ? 'Marked paid — the customer has been told in the chat.'
          : 'Payment updated'
      )
      setDraft(d => ({ ...d, [txn.id]: undefined }))
      load()
      if (onChanged) onChanged()
    } else {
      const data = res.error && res.error.response && res.error.response.data
      errorNotification(
        (data && (data.status || data.detail)) || 'Could not update that payment'
      )
    }
  }

  return (
    <Card
      title={title || 'Payments'}
      size="small"
      style={{ marginBottom: 16 }}
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={load}
        >
          Refresh
        </Button>
      }
    >
      {rows.length ? (
        rows.map(t => (
          <div
            key={t.id}
            style={{
              marginBottom: 10,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8
            }}
          >
            <Tag color={txnColor(t.status)}>{t.status}</Tag>
            <span style={{ fontWeight: 600 }}>
              {t.currency} {t.amount}
            </span>
            {t.paymentMode ? (
              <Tag color={PAYMENT_MODE_COLORS[t.paymentMode] || 'default'}>
                {PAYMENT_MODE_LABELS[t.paymentMode] || t.paymentMode}
              </Tag>
            ) : null}
            {/* The one thing the person checking the statement needs: who says they sent it. */}
            {claimedName(t.note) ? (
              <span style={{ color: '#888', fontSize: 12 }}>
                claimed by {claimedName(t.note)}
              </span>
            ) : null}
            <code style={{ fontSize: 12 }}>{t.reference}</code>
            <Space>
              <Select
                size="small"
                style={{ width: 150 }}
                value={draft[t.id] || t.status}
                onChange={v => setDraft(d => ({ ...d, [t.id]: v }))}
              >
                {STATUS_OPTIONS.map(s => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>
              <Button
                size="small"
                type="primary"
                loading={savingId === t.id}
                disabled={(draft[t.id] || t.status) === t.status}
                onClick={() => save(t)}
              >
                Save
              </Button>
            </Space>
          </div>
        ))
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No payments on this one yet"
        />
      )}
    </Card>
  )
}

ServiceTransactions.propTypes = {
  service: PropTypes.string.isRequired,
  reference: PropTypes.string,
  onChanged: PropTypes.func,
  title: PropTypes.string
}

export default ServiceTransactions
